import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES, IMAGES } from "@/lib/data";
import { MaskedLines } from "@/components/Reveal";

const Services = () => {
  return (
    <section id="services" className="relative bg-[#0F1E3D] py-24 lg:py-40 overflow-hidden" data-testid="services-section">
      <div className="grain-overlay" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-16 lg:mb-24">
          <div className="lg:col-span-8">
            <p className="eyebrow text-[#C89933] mb-6">What We Do</p>
            <h2 className="font-serif-display text-[#FAFAF8] text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight">
              <MaskedLines lines={["Eight disciplines.", "One stewardship."]} />
            </h2>
          </div>
          <p className="lg:col-span-4 text-[#FAFAF8]/60 text-base leading-relaxed font-light">
            A complete, conflict-free advisory practice that spans your entire wealth lifecycle, from your very first plan to a lasting legacy.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: (i % 2) * 0.08 }}
              className={`group relative ${s.span} rounded-sm border border-white/10 bg-white/[0.03] p-8 lg:p-10 hover:bg-white/[0.06] hover:border-[#C89933]/40 transition-colors duration-500`}
              data-testid={`service-${s.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <span className="overline text-[#C89933]/70 text-[0.6rem]">{String(i + 1).padStart(2, "0")}</span>
                <ArrowUpRight className="h-5 w-5 text-[#FAFAF8]/30 group-hover:text-[#C89933] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-400" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif-display text-[#FAFAF8] text-2xl lg:text-[2rem] leading-tight tracking-tight mt-8 mb-4">
                {s.title}
              </h3>
              <p className="text-[#FAFAF8]/55 text-base leading-relaxed font-light max-w-md">
                {s.desc}
              </p>
            </motion.div>
          ))}

          {/* Feature image tile */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-12 relative rounded-sm overflow-hidden h-64 lg:h-80 mt-1"
            data-testid="services-feature-image"
          >
            <motion.img
              src={IMAGES.services}
              alt="Contemporary building facade"
              className="absolute inset-0 h-full w-full object-cover"
              initial={{ scale: 1.15 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
            />
            <div className="absolute inset-0 bg-[#0F1E3D]/60" />
            <div className="relative h-full flex items-center px-8 lg:px-16">
              <p className="font-serif-display text-[#FAFAF8] text-2xl lg:text-4xl max-w-2xl leading-snug tracking-tight">
                Built on structure, not speculation. Every allocation is designed to compound quietly across decades.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Services;
